"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UPDATE_DEMOGRAPHICS_REQUEST_EXAMPLE = exports.updateDemographicsRequestSchema = exports.updatePatientReferralSchema = exports.updatePatientCoverageSchema = exports.updatePatientEmergencyContactSchema = exports.updatePatientIdentityDocumentSchema = exports.identityDocumentTypeCodeEnum = exports.updatePatientAddressInputSchema = void 0;
/**
 * `updateDemographics` — correcting a registered patient's record. Began as a
 * contract-first design (the contract was frozen before any implementation);
 * `UpdatePatientDemographicsService` now implements it. `expectedLockVersion`
 * is required — `Patient` has a real optimistic-lock column
 * (`docs/database/DATABASE-VERSIONING.md`), so any update contract touching it
 * must carry the version the client last read.
 */
const zod_1 = require("zod");
/**
 * WHAT `null` MEANS HERE. Every optional field is three-state: ABSENT leaves the
 * stored value alone, a VALUE replaces it, `null` CLEARS it. `.optional()`
 * alone cannot express the third state, and without it the desk could correct
 * a wrong marital status but never remove one entered by mistake.
 *
 * ADDITIVE. `salutationId` .. `address` were added after the contract was first
 * frozen (it named only the six core demographics); every field is optional and
 * the original shape still validates, so no existing caller is affected.
 */
const clearableId = zod_1.z.string().uuid().nullable().optional();
const clearableText = (max) => zod_1.z.string().trim().max(max).nullable().optional();
/** Where the patient lives. Replaces the CURRENT address; the superseded row is closed, never overwritten. */
exports.updatePatientAddressInputSchema = zod_1.z.object({
    addressLine1: zod_1.z.string().trim().min(1).max(255),
    addressLine2: clearableText(255),
    areaId: clearableId,
    countryId: zod_1.z.string().uuid(),
    stateId: clearableId,
    cityId: clearableId,
    foreignStateName: clearableText(150),
    foreignCityName: clearableText(150),
    postalCode: clearableText(20),
});
const PHONE_PATTERN = /^\+?[0-9 ()-]{5,20}$/;
const phoneText = zod_1.z.string().trim().regex(PHONE_PATTERN, 'Enter a valid phone number');
/**
 * PATEDIT-ALL (desk, 2026-10-18: "anything can be edited except the OP number ... user will mistake all ways, extend the present edit flow
 * to fill all fields"). Everything the registration form collects now travels through this same request:
 *
 *   - `isNewborn`, `isEmployeeDependent`, `employeeId`     columns on the patient;
 *   - `alternateMobile`                                    the second, non-primary phone;
 *   - `occupation`, `smokingStatusId`, `alcoholUseStatusId`  the social-history row;
 *   - `preferredChannelId`, `preferredLanguageId`, `communicationConsent`  the communication preference;
 *   - `emergencyContact`                                   the first emergency contact (null removes it);
 *   - `identityDocuments`                                  government ID numbers (null removes one). Sent in plain text over the wire
 *                                                          ONCE and sealed (encrypted + hashed + masked) before the request is stored — the
 *                                                          approver reads only the masked number;
 *   - `isMlc`, `mlcNumber`                                 the medico-legal-case flag;
 *   - `generalConsent`, `privacyConsent`, `consentScope`   the two consents the registration form records;
 *   - `coverage`                                           the payer / policy the patient is registered under (null ends it = self pay);
 *   - `referral`                                           the inbound referral recorded at registration (null removes it).
 *
 * Same three-state rule as everything above: ABSENT leaves it alone, a VALUE sets it, `null` clears it (booleans are only ever true / false).
 */
exports.identityDocumentTypeCodeEnum = zod_1.z.enum(['AADHAAR', 'PAN', 'PASSPORT', 'DRIVING_LICENSE', 'VOTER_ID', 'ABHA']);
exports.updatePatientIdentityDocumentSchema = zod_1.z.object({
    typeCode: exports.identityDocumentTypeCodeEnum,
    /** The number as printed on the document. `null` removes the document of this type from the record. */
    documentNumber: zod_1.z.string().trim().min(1).max(50).nullable(),
});
exports.updatePatientEmergencyContactSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(255),
    relationshipTypeId: zod_1.z.string().uuid(),
    phone: phoneText,
    alternatePhone: phoneText.nullable().optional(),
});
/** The patient's primary payer. Only the parts that change need to be sent; a patient with no payer needs payer, policy number and relation. */
exports.updatePatientCoverageSchema = zod_1.z.object({
    payerId: zod_1.z.string().uuid().optional(),
    policyNumber: zod_1.z.string().trim().min(1).max(50).optional(),
    groupNumber: clearableText(100),
    relationshipToSubscriberId: zod_1.z.string().uuid().optional(),
    cashlessEligible: zod_1.z.boolean().optional(),
    preAuthRequired: zod_1.z.boolean().optional(),
});
exports.updatePatientReferralSchema = zod_1.z.object({
    referringFacilityName: zod_1.z.string().trim().min(1).max(255),
    referringProviderName: clearableText(255),
    reason: zod_1.z.string().trim().min(1).max(2000),
});
exports.updateDemographicsRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid(),
    expectedLockVersion: zod_1.z.number().int().nonnegative(),
    givenName: zod_1.z.string().trim().min(1).max(150).optional(),
    middleName: clearableText(150),
    familyName: clearableText(150),
    dateOfBirth: zod_1.z.coerce.date().nullable().optional(),
    dateOfBirthPrecision: zod_1.z.enum(['EXACT', 'YEAR_ONLY', 'ESTIMATED']).optional(),
    genderId: clearableId,
    maritalStatusId: clearableId,
    bloodGroupId: clearableId,
    religionId: clearableId,
    nationalityId: clearableId,
    salutationId: clearableId,
    qualificationId: clearableId,
    relationTypeId: clearableId,
    relationName: clearableText(255),
    /** The primary phone. Other phone numbers on file are left as they are. */
    mobile: zod_1.z
        .string()
        .trim()
        .regex(/^\+?[0-9 ()-]{5,20}$/, 'Enter a valid phone number')
        .nullable()
        .optional(),
    email: zod_1.z.string().trim().email().max(255).nullable().optional(),
    address: exports.updatePatientAddressInputSchema.optional(),
    // ---- PATEDIT-ALL: the rest of what registration collects -------------------------------------------------------------------------
    isNewborn: zod_1.z.boolean().optional(),
    isEmployeeDependent: zod_1.z.boolean().optional(),
    /** The staff member a dependent belongs to. Cleared automatically when the dependent tick is removed. */
    employeeId: clearableId,
    /** The second phone number (the primary one is `mobile`). */
    alternateMobile: phoneText.nullable().optional(),
    occupation: clearableText(150),
    smokingStatusId: clearableId,
    alcoholUseStatusId: clearableId,
    /** SMS / e-mail / WhatsApp. Clearing it removes the whole communication preference (language and consent cannot exist without it). */
    preferredChannelId: clearableId,
    preferredLanguageId: clearableId,
    /** True = the patient may be contacted. */
    communicationConsent: zod_1.z.boolean().optional(),
    emergencyContact: exports.updatePatientEmergencyContactSchema.nullable().optional(),
    identityDocuments: zod_1.z.array(exports.updatePatientIdentityDocumentSchema).max(6).optional(),
    isMlc: zod_1.z.boolean().optional(),
    mlcNumber: clearableText(255),
    generalConsent: zod_1.z.boolean().optional(),
    privacyConsent: zod_1.z.boolean().optional(),
    /** What the consent covers — required when a consent is being granted. */
    consentScope: clearableText(1000),
    coverage: exports.updatePatientCoverageSchema.nullable().optional(),
    referral: exports.updatePatientReferralSchema.nullable().optional(),
    /** Why the record was corrected — kept on the audit entry. */
    reason: zod_1.z.string().trim().max(500).optional(),
});
exports.UPDATE_DEMOGRAPHICS_REQUEST_EXAMPLE = {
    patientId: '22222222-2222-2222-2222-222222222222',
    expectedLockVersion: 3,
    familyName: 'Rao-Sharma',
};
//# sourceMappingURL=demographics.request.js.map